// Shared game data types. Concrete definitions live in @vm/content.

export const WeaponSlot = {
  Primary: 0,
  Backup: 1,
  Melee: 2,
  Heavy: 3,
  Sniper: 4,
  /** Mode-granted tool (builder) — never part of a player loadout. */
  Tool: 5,
} as const;
export type WeaponSlot = (typeof WeaponSlot)[keyof typeof WeaponSlot];
export const SLOT_COUNT = 6;
export const LOADOUT_SLOTS = 5;
export const SLOT_NAMES = ['Primary', 'Backup', 'Melee', 'Heavy', 'Sniper', 'Tool'] as const;

export type FireKind = 'hitscan' | 'projectile' | 'melee' | 'beam' | 'builder';
export type Rarity = 'common' | 'rare' | 'epic' | 'legendary' | 'mythic';
export type Currency = 'coins' | 'gems';

export interface ExplosionDef {
  /** Player damage radius, meters. */
  radius: number;
  /** Damage at the center; linear falloff to 25% at the edge. */
  damage: number;
  /** Voxel damage at the center (world material hp is 1..60). */
  voxelPower: number;
  /** Radius of the voxel crater, meters. */
  voxelRadius: number;
  /** Knockback speed at the center, m/s. */
  knockback: number;
  /** Multiplier for damage dealt to the shooter (rocket jumping). */
  selfDamage?: number;
}

export interface ProjectileDef {
  speed: number;
  /** Gravity multiplier (1 = normal gravity). */
  gravity: number;
  /** Collision radius vs players, meters. */
  radius: number;
  /** Seconds before auto-detonation. */
  fuse: number;
  /** Restitution on world hits; 0 = explode/stop on first impact. */
  bounce: number;
  sticky: boolean;
  /** Direct-hit damage in addition to the explosion. */
  impactDamage: number;
  explosion: ExplosionDef | null;
  /** Visual style key for the client. */
  visual: 'rocket' | 'grenade' | 'plasma' | 'nail' | 'flare' | 'block' | 'orb' | 'fireball';
  /** Burning damage per second applied on hit (flare/fire weapons). */
  burn?: number;
  /** Splits into bomblets when it explodes. */
  cluster?: { count: number; def: ProjectileDef };
}

export interface MeleeDef {
  range: number;
  /** Voxel crater radius at the impact point, meters. */
  voxelRadius: number;
  voxelPower: number;
  /** Seconds between the swing start and the hit. */
  windup: number;
}

export interface BeamDef {
  range: number;
  /** Player damage per second. */
  dps: number;
  /** Voxel damage per second at the beam tip. */
  voxelDps: number;
  /** Voxel radius at the tip, meters. */
  voxelRadius: number;
  /** Chain to additional players within this radius (tesla). */
  chain?: number;
  /** Visual style. */
  visual: 'laser' | 'mining' | 'flame' | 'tesla' | 'heal';
  /** Burn damage per second applied after contact (flamethrower). */
  burn?: number;
}

export interface BuilderDef {
  /** Edge length of placed blocks, in voxels. */
  block: number;
  /** World material name placed. */
  material: string;
}

/** Parameters for the procedural voxel weapon model. */
export interface WeaponModelSpec {
  archetype:
    | 'pistol' | 'revolver' | 'smg' | 'rifle' | 'carbine' | 'shotgun' | 'lmg' | 'minigun'
    | 'sniper' | 'dmr' | 'railgun' | 'rocket' | 'grenadelauncher' | 'flamethrower' | 'laser'
    | 'plasma' | 'tesla' | 'nailgun' | 'mininglaser' | 'blockgun' | 'hammer' | 'sword'
    | 'pickaxe' | 'axe' | 'katana' | 'buzzblade' | 'flare' | 'crossbow';
  primary: number;
  secondary: number;
  accent: number;
  glow?: number;
}

export interface WeaponDef {
  id: string;
  /** Compact numeric id used on the wire. Stable once shipped. */
  num: number;
  name: string;
  description: string;
  slot: WeaponSlot;
  kind: FireKind;
  rarity: Rarity;
  auto: boolean;
  /** Rounds per burst (burst weapons). */
  burst?: number;
  rpm: number;
  mag: number;
  reserve: number;
  /** Reload time in seconds. */
  reload: number;
  /** Damage per bullet/pellet (hitscan) or per swing (melee). */
  damage: number;
  pellets?: number;
  /** Hip-fire spread cone half-angle, degrees. */
  spread: number;
  /** Spread while aiming. */
  aimSpread?: number;
  /** Extra spread while moving at full speed. */
  moveSpread?: number;
  range: number;
  falloffStart?: number;
  falloffEnd?: number;
  /** Damage multiplier at falloffEnd and beyond. */
  falloffMin?: number;
  /** Headshot multiplier override. */
  headMult?: number;
  /** Voxel damage per bullet/pellet. */
  voxelPower: number;
  /** Radius of voxels damaged around a bullet impact, meters (0 = single voxel). */
  voxelRadius?: number;
  /** Total voxel hp a bullet can tunnel through before stopping. */
  penetration?: number;
  projectile?: ProjectileDef;
  melee?: MeleeDef;
  beam?: BeamDef;
  builder?: BuilderDef;
  /** Camera kick per shot, degrees. */
  recoil: number;
  /** Field-of-view multiplier when aiming (<1 zooms). */
  zoom?: number;
  scope?: boolean;
  /** Movement speed multiplier while held. */
  moveSpeed: number;
  equipTime: number;
  /** Charge time before firing (plasma/railgun), seconds. */
  charge?: number;
  unlockLevel: number;
  price: number;
  currency: Currency;
  model: WeaponModelSpec;
  /** Sound bank key. */
  sound: string;
  /** Tracer color. */
  tracer?: number;
  /** Only available through modes (builder tool, zombie claws). */
  hidden?: boolean;
}

export type GadgetKind = 'frag' | 'sticky' | 'medkit' | 'jetpack' | 'wall' | 'stim' | 'cluster' | 'shield';

export interface GadgetDef {
  id: string;
  num: number;
  name: string;
  description: string;
  kind: GadgetKind;
  cooldown: number;
  unlockLevel: number;
  price: number;
  currency: Currency;
  /** Throwables. */
  projectile?: ProjectileDef;
  /** Medkit heal amount / stim duration / wall size etc. */
  amount?: number;
  duration?: number;
  icon: string;
  color: number;
}

export type ModeId =
  | 'ffa'
  | 'tdm'
  | 'dom'
  | 'ctf'
  | 'demolition'
  | 'siege'
  | 'br'
  | 'zombies'
  | 'duel'
  | 'gungame'
  | 'sandbox'
  | 'practice'
  | 'tutorial';

export interface Loadout {
  /** Weapon ids per slot (index = WeaponSlot). Empty string = none. */
  weapons: string[];
  gadget: string;
  /** Companion pet id; empty/missing = none. */
  pet?: string;
}

export type PetRole = 'attacker' | 'guard' | 'scout' | 'digger' | 'medic';

/**
 * Companion creature: an earned loadout slot (unlocked by level, bought with coins; only its looks
 * are sold). Pets hover beside their owner, are made of voxels and can be shot apart; they re-form
 * `reform` seconds later. Roles are balanced as sidegrades.
 */
export interface PetDef {
  id: string;
  /** Wire id (list order: append, never reorder). */
  num: number;
  name: string;
  role: PetRole;
  description: string;
  unlockLevel: number;
  price: number;
  currency: Currency;
  hp: number;
  /** Seconds to re-form after being destroyed. */
  reform: number;
  /**
   * Role tuning. attacker: bite `damage` every `interval` s at enemies within `range` m of the owner.
   * guard: absorbs `amount` (0..1) of damage the owner takes. scout: every `interval` s marks enemies
   * within `range` m for `duration` s. digger: when the owner shoots the world within `range` m, chews
   * a `radius` m crater of `amount` voxel power every `interval` s. medic: heals `amount` hp/s after
   * `duration` s without damage.
   */
  damage?: number;
  interval?: number;
  range?: number;
  amount?: number;
  duration?: number;
  radius?: number;
  /** Default colors [primary, secondary, accent] (styles replace them). */
  colors: [number, number, number];
}

export interface Cosmetics {
  /** Base64 skin data (SKIN_BYTES) or a preset id prefixed with "preset:". */
  skin: string;
  armor: string;
  /** Weapon camo per weapon id. */
  camos?: Record<string, string>;
  /** Bullet tracer style per (hitscan) weapon id. Purely cosmetic. */
  tracers?: Record<string, string>;
  /** Weapon sight per weapon id (see packages/content/src/scopes.ts). Changes zoom and aiming speed, never damage. */
  scopes?: Record<string, string>;
  deathFx?: string;
  banner?: string;
  /** Pet style id (recolors the equipped pet). */
  petStyle?: string;
}

/** Public per-player info broadcast on join and on changes. */
export interface PlayerInfo {
  id: number;
  name: string;
  team: number;
  level: number;
  clanTag?: string;
  isBot: boolean;
  /** Account id (humans) for friend/report actions. */
  userId?: string;
  loadout: Loadout;
  cosmetics: Cosmetics;
}

export interface MatchSettings {
  mode: ModeId;
  mapId: string;
  timeLimit: number;
  scoreLimit: number;
  maxPlayers: number;
  teams: number;
  friendlyFire: boolean;
  /** Fill empty slots with bots. */
  bots: number;
  botSkill: number;
  private: boolean;
  roomCode?: string;
  /** Building allowed (siege, sandbox, zombies...). */
  building: boolean;
  /** Round-based modes. */
  rounds?: number;
  /** Live weapon balance (admin `weapons.balance`), applied by the server and every client in the match. */
  weapons?: MatchWeaponConfig;
  /** Record highlight replays (admin `highlights.capture`); game servers only. */
  capture?: { minScore: number; perMatch: number };
}

/** Weapon stat overrides and kill-switched weapons for one match (see WEAPON_TUNING_FIELDS in @vm/content). */
export interface MatchWeaponConfig {
  /** Weapon id → stat key → value. */
  overrides?: Record<string, WeaponTuning>;
  /** Weapons taken out of play: never spawned, looted or handed to bots; loadouts fall back to another weapon of the slot. */
  disabled?: string[];
}

/** Stat key (e.g. 'damage', 'rpm', 'explosion.radius') → value. */
export type WeaponTuning = Record<string, number>;

export interface ScoreLine {
  id: number;
  name: string;
  team: number;
  kills: number;
  deaths: number;
  assists: number;
  score: number;
  isBot: boolean;
  userId?: string;
  ping: number;
}

export interface MatchPlayerStats {
  userId?: string;
  playerId: number;
  name: string;
  team: number;
  isBot: boolean;
  kills: number;
  deaths: number;
  assists: number;
  score: number;
  headshots: number;
  damage: number;
  voxelsDestroyed: number;
  voxelsBuilt: number;
  objectives: number;
  timePlayed: number;
  won: boolean;
  placement: number;
  /** Kills per weapon id. */
  weaponKills: Record<string, number>;
  longestStreak: number;
  collapseKills: number;
  zombiesKilled: number;
  wavesSurvived: number;
  /** Takedowns of bots, and deaths at the hands of bots (bot difficulty analytics). */
  botKills?: number;
  deathsByBots?: number;
}

export interface MatchResult {
  matchId: string;
  mode: ModeId;
  mapId: string;
  durationSec: number;
  winnerTeam: number;
  players: MatchPlayerStats[];
}

/** Signed by the API, verified by game servers (HS256 with the shared game secret). */
export interface GameTicket {
  /** Account id. */
  sub: string;
  name: string;
  level: number;
  clanTag?: string;
  loadout: Loadout;
  cosmetics: Cosmetics;
  owned?: string[];
  restricted?: boolean;
  /** Match to join. */
  match: string;
  team?: number;
  partyId?: string;
}

/** Game server -> API heartbeat. */
export interface ServerHeartbeat {
  id: string;
  url: string;
  region: string;
  capacity: number;
  load: number;
  matches: {
    id: string;
    mode: ModeId;
    mapId: string;
    humans: number;
    bots: number;
    maxPlayers: number;
    open: boolean;
    private: boolean;
    roomCode?: string;
    timeLeft?: number;
    /** Where players connect for this match, when not the server's own `url` (a match worker's port). */
    url?: string;
  }[];
}
